import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Breadcrumbs,
  Button,
  CardComponent,
  DocComparison,
  DocExtractData,
  DocParameter,
  DocReview,
  DocStepper,
  DocUpload,
  PageTitleAndSubTitle,
  TextField,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import ReportCreateNewLabelField from '../../_components/atoms/ReportCreateNewLabelField';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { commonDropDownProps } from '../../_components/atoms/ReportsCohortItem';
import { DOC_COMPARISON, HOME_SCREEN } from '../../core-utils/routes';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import properties from '../../core-utils/properties';
import Snackbar from '../../_components/atoms/Snackbar';
import newComparisonMessages from '../../core-utils/messages/NewComparisonConstants';
import { comparisonObjectProps } from '../../core-utils/helper-utils/docComparisonHelper';
import useSnackbar from '../../core-utils/custom-hooks/Snackbar';
import { getServiceAgreements } from '../../core-utils/axios/compliance-assurance/contracts';
import { DocumentsSelection } from '../../_components/organisms/DocumentsSelection';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import {
  formatToRowData,
  ParametersSelection,
} from '../../_components/organisms/ParametersSelection';
import AutocompleteWithCheckBoxes from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Autocomplete';
import OnBoardingPopUp from '../../_components/molecules/OnBoardingPopup';

const useStyles = makeStyles()(() => ({
  breadCrumbsGrid: {
    margin: '10px',
  },
  hr: {
    width: '100%',
    border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    margin: '18px 0px',
  },
  mainDiv: {
    width: '1276px',
    padding: '20px 0px 36px 63px',
  },
  innerInputContainer: {
    display: 'flex',
    gap: '8px',
  },
  stepperGrid: {
    marginBottom: '40px',
  },
  buttonGrid: {
    marginTop: '50px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '5px',
    paddingBottom: '10px',
  },
  comparisonDetailsGrid: {
    display: 'flex',
    gap: '200px',
  },
}));

interface LocationState {
  comparison_names?: string[];
}

const NewComparisonPage: React.FC = observer(({}) => {
  //Constants
  const navigate = useNavigate();
  const { classes } = useStyles();
  const location = useLocation() as { state: LocationState };
  const breadcrumbItems = [
    [
      'text',
      'Home',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => {
        navigate(HOME_SCREEN);
      },
    ],
    [
      'text',
      'Document Comparer',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 600,
      },
      () => {
        navigate(DOC_COMPARISON);
      },
    ],
    [
      'text',
      'New Comparison',
      {
        color: patientAccessTheme.palette.text.highemphasis,
        fontWeight: 600,
      },
    ],
  ];
  const { pageTitles, cardTitles } = newComparisonMessages;
  const { openSnackbar, snackbarState, handleCloseSnackBar } = useSnackbar();
  const comparisonNames = location?.state?.comparison_names || [];

  // States
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [vendorTypes, setVendorTypes] = useState<string[]>([]);
  const steps = [
    {
      icon: (
        <DocUpload highlighted={currentTab === 0} completed={currentTab > 0} />
      ),
      title: 'Upload Docket',
      isActive: currentTab === 0,
      isCompleted: currentTab > 0,
    },
    {
      icon: (
        <DocComparison
          highlighted={currentTab === 1}
          completed={currentTab > 1}
        />
      ),
      title: 'Select Comparison',
      isActive: currentTab === 1,
      isCompleted: currentTab > 1,
    },
    {
      icon: (
        <DocParameter
          highlighted={currentTab === 2}
          completed={currentTab > 2}
        />
      ),
      title: 'Select Parameters',
      isActive: currentTab === 2,
      isCompleted: currentTab > 2,
    },
    {
      icon: (
        <DocExtractData
          highlighted={currentTab === 3}
          completed={currentTab > 3}
        />
      ),
      title: 'Extracting Data',
      isActive: currentTab === 3,
      isCompleted: currentTab > 3,
    },
    {
      icon: <DocReview highlighted={currentTab === 4} completed={false} />,
      title: 'Data Ready For Review',
      isActive: currentTab === 4,
      isCompleted: false,
    },
  ];
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState<boolean>(
    true,
  );
  const [parametersOptions, setParametersOptions] = useState<any[]>([]);
  const [selectedContracts, setSelectedContracts] = useState<any[]>([]);
  const [selectedParameters, setSelectedParameters] = useState<any[]>([]);
  const [
    comparisonObject,
    setComparisonObject,
  ] = useState<comparisonObjectProps>({
    comparison_name: '',
    vendor_type: '',
    contract_ids: [],
    comparison_parameters: [],
    status: '',
    vendor_id: '',
    vendor_identifier: '',
  });

  //API calls
  const fetchServiceAgreements = async () => {
    const vendorTypes = await getServiceAgreements(openSnackbar);
    setVendorTypes(
      vendorTypes?.map((item: any) => item.service_agreement_name) || [],
    );
  };

  const createDocComparison = async () => {
    const updatedSelectedContracts = selectedParameters.map((item, index) => ({
      parameter: item.name,
      description: item.value,
      type: item.type,
      position: index + 1,
      category: item.category,
      subParameter: item?.subrows?.map((subItem: any, subIndex: number) => ({
        name: subItem.name,
        description: subItem.value,
        key: subItem.key ?? subItem.name?.toLowerCase().replace(/\s+/g, '_'),
        favorability_range: subItem.favorability,
        response_type: subItem.type,
        position: subIndex + 1,
      })),
    }));

    const payload = {
      comparison_name: comparisonObject.comparison_name,
      vendor_type: comparisonObject.vendor_type,
      documents:
        selectedContracts?.map((item) => ({
          contract_id: item.record_type === 'draft' ? null : item.contract_id,
          eg_nid: item.eg_nid,
          vendor_name: item.health_care_partner_name,
          record_type: item.record_type ?? 'contract',
          document_name: item.document.document_name,
          document_url: item.document.document_url,
          ...(item.record_type === 'draft' ? { draft_id: item.draft_id } : {}),
        })) || [],
      comparison_parameters: updatedSelectedContracts,
    };

    try {
      const response = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.createDocComparison,
        payload,
      );
      if (response.status === 200) {
      } else {
        openSnackbar('Something went wrong', 'error', 3000);
        throw new Error('Unexpected response status'); // Handle unexpected status codes
      }
    } catch (err) {
      openSnackbar('Failed to create the comparison document', 'error', 3000);
    }
  };

  // Api calls
  const getDefaultParameters = async () => {
    try {
      const response = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getDefaultParameters,
        {
          service_agreement_name: comparisonObject?.vendor_type,
        },
      );
      const grouped: any = [];
      response?.data?.data.forEach((item: any) => {
        const group = grouped.find((g: any) => g.clause === item.clause);
        const provision = {
          key: item.key,
          provision_name: item.provision_name,
          assessment_description: item.assessment_description,
          type: item.response_type,
          favorability: item.favorability,
          explanation: item.explanation,
          sample: item.sample,
          impact: item.impact,
        };

        if (group) {
          group.provisions.push(provision);
        } else {
          grouped.push({
            clause: item.clause,
            category: item.category,
            provisions: [provision],
          });
        }
      });
      const groupedWithId = grouped.map((item: any, index: number) => ({
        ...item,
        id: index + 1,
      }));
      setParametersOptions(groupedWithId);
    } catch (error: any) {
      openSnackbar(
        error?.response?.data?.message || 'Error fetching default parameters',
        'error',
        2000,
      );
    }
  };

  //UseEffects
  useEffect(() => {
    fetchServiceAgreements();
  }, []);

  useEffect(() => {
    if (currentTab === 0) {
      setIsNextButtonDisabled(
        !(comparisonObject?.comparison_name && comparisonObject?.vendor_type),
      );
    } else if (currentTab === 1) {
      setIsNextButtonDisabled(selectedContracts.length < 1);
      getDefaultParameters();
    } else if (currentTab === 2) {
      setIsNextButtonDisabled(selectedParameters.length === 0);
    } else if (currentTab === 3) {
      createDocComparison();
    } else {
      setIsNextButtonDisabled(true);
    }
  }, [
    currentTab,
    comparisonObject?.comparison_name,
    comparisonObject?.vendor_type,
    selectedContracts,
  ]);

  useEffect(() => {
    if (parametersOptions?.length && selectedParameters.length === 0) {
      const allFormatted = formatToRowData(parametersOptions);
      setSelectedParameters(allFormatted);
    }
  }, [parametersOptions]);

  //Components
  const ComparisonDetails = () => {
    return (
      <CardComponent
        title={''}
        body={
          <Grid className={classes.comparisonDetailsGrid}>
            <Grid>
              <Typography
                fontType="medium"
                fontSize="12px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
              >
                Comparison Name
              </Typography>
              <Typography
                fontType="medium"
                fontSize="14px"
                textColor={patientAccessTheme.palette.text.highemphasis}
              >
                {comparisonObject.comparison_name}
              </Typography>
            </Grid>
            <Grid>
              <Typography
                fontType="medium"
                fontSize="12px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
              >
                Vendor Type
              </Typography>
              <Typography
                fontType="medium"
                fontSize="14px"
                textColor={patientAccessTheme.palette.text.highemphasis}
              >
                {comparisonObject.vendor_type}
              </Typography>
            </Grid>
            {currentTab === 2 && selectedContracts.length > 0 && (
              <Grid>
                <Typography
                  fontType="medium"
                  fontSize="12px"
                  textColor={patientAccessTheme.palette.text.mediumemphasis}
                >
                  Selected Documents
                </Typography>
                <AutocompleteWithCheckBoxes
                  defaultValue={selectedContracts?.map(
                    (contract) => contract?.document?.document_name || '',
                  )}
                  options={selectedContracts?.map(
                    (contract) => contract?.document?.document_name || '',
                  )}
                  handleChange={() => {}}
                />
              </Grid>
            )}
          </Grid>
        }
      />
    );
  };

  const FillComparisonDetails = () => {
    return (
      <CardComponent
        title={cardTitles.card1.title}
        body={
          <Grid>
            <hr className={classes.hr} />
            <Grid className={classes.innerInputContainer}>
              <ReportCreateNewLabelField
                label="Comparison Name"
                width="50%"
                component={
                  <TextField
                    value={comparisonObject.comparison_name}
                    placeholder="Enter Comparison Name"
                    height="36px"
                    width="100%"
                    fullWidth
                    onChange={(e: any) => {
                      setComparisonObject((prev) => ({
                        ...prev,
                        comparison_name: e.target.value,
                      }));
                    }}
                  />
                }
                marginBottom="12px"
              />
              <ReportCreateNewLabelField
                label={'Vendor Type'}
                component={
                  <Dropdown
                    label="Select Vendor Type"
                    dropDownOptions={vendorTypes}
                    dropDownValues={vendorTypes}
                    value={comparisonObject.vendor_type}
                    handleChange={(event) => {
                      const selectedValue = event.target.value as any;
                      setComparisonObject((prev) => ({
                        ...prev,
                        vendor_type: selectedValue,
                      }));
                    }}
                    style={{
                      width: '547px',
                    }}
                    selectStyles={{
                      ...commonDropDownProps.selectStyles,
                      width: '100%',
                    }}
                    labelTop="10px"
                  />
                }
              />
            </Grid>
          </Grid>
        }
      />
    );
  };

  const SelectComparisons = () => {
    return (
      <>
        {ComparisonDetails()}
        <DocumentsSelection
          setSelectedContracts={setSelectedContracts}
          vendorType={comparisonObject.vendor_type}
          comparisonName={comparisonObject?.comparison_name}
        />
      </>
    );
  };

  const AddParameters = () => {
    return (
      <>
        {ComparisonDetails()}
        <ParametersSelection
          vendorType={comparisonObject.vendor_type}
          selectedParameters={selectedParameters}
          setSelectedParameters={setSelectedParameters}
          parametersOptions={parametersOptions}
        />
      </>
    );
  };

  const ProcessingDocuments = () => {
    return (
      <OnBoardingPopUp
        title="Documents are being processed"
        subTitle="This may take a few minutes. You can navigate to other sections and come back later."
        statusTitles={[]}
        locateTitle={'Click Here'}
        locateLink={DOC_COMPARISON}
        infoLabel='You can return to the "Document Comparison" page to check the status.'
        rootStyles={{
          border: 'none',
          padding: '0px 10px',
        }}
      />
    );
  };

  const getStepsContent = () => {
    switch (currentTab) {
      case 0:
        return FillComparisonDetails();
      case 1:
        return SelectComparisons();
      case 2:
        return AddParameters();
      case 3:
        return ProcessingDocuments();
      default:
        return null;
    }
  };

  return (
    <>
      <Grid className={classes.breadCrumbsGrid}>
        <Breadcrumbs itemList={breadcrumbItems} />
      </Grid>
      <hr className={classes.hr} />
      <Grid className={classes.mainDiv}>
        <PageTitleAndSubTitle title={pageTitles.title} subTitle={''} />
        <CardComponent
          title={''}
          mainGridStyles={{
            marginTop: '10px',
          }}
          body={
            <>
              <Grid className={classes.stepperGrid}>
                <DocStepper statusTitles={steps} />
              </Grid>
              {getStepsContent()}
              <Grid className={classes.buttonGrid}>
                {currentTab < 3 && (
                  <Button
                    color="primary"
                    variant="contained"
                    height="43px"
                    style={{
                      padding: '8px 20px',
                      textTransform: 'none',
                    }}
                    label={currentTab === 2 ? 'Submit' : 'Next'}
                    disabled={isNextButtonDisabled}
                    onClick={() => {
                      if (currentTab === 0) {
                        const trimmedName = comparisonObject.comparison_name?.trim();
                        const isDuplicate = comparisonNames
                          .map((name: string) => name.toLowerCase().trim())
                          .includes(trimmedName.toLowerCase());
                        if (isDuplicate) {
                          openSnackbar(
                            'Comparison name already taken',
                            'error',
                            3000,
                          );
                          return;
                        }
                      }
                      setCurrentTab((prev) => prev + 1);
                    }}
                  />
                )}
              </Grid>
            </>
          }
        />
      </Grid>
      <Snackbar
        {...snackbarState}
        vertical={'bottom'}
        horizontal={'right'}
        handleClose={handleCloseSnackBar}
      />
    </>
  );
});

export default NewComparisonPage;
